import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import AuthLayout from '@/app/layout/AuthLayout';
import RootLayout from '@/app/layout/RootLayout';
import { SliderContentPage } from '@/pages/publisher-slider-content/SliderContent';
import { VideoContentPage } from '@/pages/publisher-video-content/VideoContent';
import { commons, config, publisherRoutes } from '@/shared/lib';
import PageLoader from '@/shared/ui/PageLoader';

import RootBoundary from './layout/RootBoundary';

const PublisherLayout = lazy(() => import('./layout/PublisherLayout'));

const Ads = lazy(() => import('@/pages/publisher-ads'));
const Login = lazy(() => import('@/pages/signin'));
const Recovery = lazy(() => import('@/pages/password-recovery'));
const Confirmation = lazy(() => import('@/pages/confirmation'));
const ResendLink = lazy(() => import('@/pages/resend-link'));
const NotVerified = lazy(() => import('@/pages/not-verified'));
const PublisherDashboard = lazy(() => import('@/pages/publisher-dashboard'));
const Payments = lazy(() => import('@/pages/publisher-payments'));
const Profile = lazy(() => import('@/pages/common-profile'));
const Referrals = lazy(() => import('@/pages/publisher-referrals'));
const Registration = lazy(() => import('@/pages/signup'));
const Sites = lazy(() => import('@/pages/publisher-sites'));
const SiteAdUnits = lazy(() => import('@/pages/publisher-site-adunits'));
const Statistics = lazy(() => import('@/pages/publisher-statistics'));
const Content = lazy(() => import('@/pages/publisher-content'));
const ViadsFlow = lazy(() => import('@/pages/publisher-flow'));
const LoginAsUser = lazy(() => import('@/pages/login-as-user'));

const publisherRoute = [
  {
    path: publisherRoutes.dashboard,
    element: (
      <Suspense fallback={<PageLoader />}>
        <PublisherDashboard />
      </Suspense>
    ),
  },
  {
    path: publisherRoutes.ads,
    element: (
      <Suspense fallback={<PageLoader />}>
        <Ads />
      </Suspense>
    ),
  },
  {
    path: publisherRoutes.sites,
    element: (
      <Suspense fallback={<PageLoader />}>
        <Sites />
      </Suspense>
    ),
  },

  {
    path: publisherRoutes['site-adunits'],
    element: (
      <Suspense fallback={<PageLoader />}>
        <SiteAdUnits />
      </Suspense>
    ),
  },
  {
    path: publisherRoutes.statistics,
    element: (
      <Suspense fallback={<PageLoader />}>
        <Statistics />
      </Suspense>
    ),
  },
  {
    path: publisherRoutes.content,
    element: (
      <Suspense fallback={<PageLoader />}>
        <Content />
      </Suspense>
    ),
  },
  {
    path: publisherRoutes.sliderContent,
    element: (
      <Suspense fallback={<PageLoader />}>
        <SliderContentPage />
      </Suspense>
    ),
  },
  {
    path: publisherRoutes.videoContent,
    element: (
      <Suspense fallback={<PageLoader />}>
        <VideoContentPage />
      </Suspense>
    ),
  },
  {
    path: publisherRoutes.flow,
    element: (
      <Suspense fallback={<PageLoader />}>
        <ViadsFlow />
      </Suspense>
    ),
  },
  {
    path: publisherRoutes.payments,
    element: (
      <Suspense fallback={<PageLoader />}>
        <Payments />
      </Suspense>
    ),
  },
  {
    path: publisherRoutes.referrals,
    element: (
      <Suspense fallback={<PageLoader />}>
        <Referrals />
      </Suspense>
    ),
  },
  {
    path: publisherRoutes.profile,
    element: config.CONTENT_ONLY_BUILD ? (
      <Navigate to={publisherRoutes.content} />
    ) : (
      <Suspense fallback={<PageLoader />}>
        <Profile />
      </Suspense>
    ),
  },
  {
    path: publisherRoutes.index,
    element: (
      <Navigate
        to={
          config.CONTENT_ONLY_BUILD
            ? publisherRoutes.content
            : `${publisherRoutes.index}/${config.CABINET_MAIN_PAGE}`
        }
      />
    ),
  },
];

const router = createBrowserRouter([
  {
    path: commons.index,
    element: <RootLayout />,
    errorElement: <RootBoundary />,
    children: [
      {
        path: publisherRoutes.index,
        element: (
          <Suspense>
            <PublisherLayout />
          </Suspense>
        ),
        children: publisherRoute,
      },
      {
        path: commons.confirmation,
        element: (
          <Suspense fallback={<PageLoader />}>
            <Confirmation />
          </Suspense>
        ),
      },
      {
        path: commons['resend-email'],
        element: (
          <Suspense fallback={<PageLoader />}>
            <ResendLink />
          </Suspense>
        ),
      },
      {
        path: commons['not-verified'],
        element: (
          <Suspense fallback={<PageLoader />}>
            <NotVerified />
          </Suspense>
        ),
      },
      {
        element: <AuthLayout />,
        children: [
          {
            path: commons.signin,
            element: (
              <Suspense fallback={<PageLoader />}>
                <Login />
              </Suspense>
            ),
          },
          {
            path: commons.signup,
            element: (
              <Suspense fallback={<PageLoader />}>
                <Registration />
              </Suspense>
            ),
          },
          {
            path: commons['password-recovery'],
            element: (
              <Suspense fallback={<PageLoader />}>
                <Recovery />
              </Suspense>
            ),
          },
          {
            path: commons.index,
            element: <Navigate to={commons.signin} />,
          },
        ],
      },
      {
        path: commons['login-as-user'],
        element: (
          <Suspense fallback={<PageLoader />}>
            <LoginAsUser />
          </Suspense>
        ),
      },
    ],
  },
]);

export default router;
